import type {
  DashboardOverallProgress,
  DashboardProgressSegmentKey,
  DashboardUserWorkload,
} from "@models";
import { InfoOutlined } from "@mui/icons-material";
import { IconButton, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { docStatusDictionary } from "src/utils/documents";
import { DashboardEmptyStateText } from "./DashboardEmptyStateText";

type DashboardOverallProgressCardProps = {
  title: string;
  progress?: DashboardOverallProgress;
  userWorkload?: DashboardUserWorkload;
  firstName?: string | null;
  className?: string;
};

type DashboardProgressSegment = {
  key: DashboardProgressSegmentKey;
  label: string;
  count: number;
  colorHex: string;
  testIdKey: string;
};

const SEGMENT_COLORS: Record<DashboardProgressSegmentKey, string> = {
  inDraft: docStatusDictionary.DRAFT.borderColor,
  routing: docStatusDictionary.UNAPPROVED.borderColor,
  live: docStatusDictionary.LIVE.borderColor,
  remaining: "#e5e7eb",
};

const SEGMENT_TEST_ID_KEYS: Record<DashboardProgressSegmentKey, string> = {
  inDraft: "draft",
  routing: "waiting",
  live: "live",
  remaining: "remaining",
};

const FALLBACK_SEGMENTS: DashboardOverallProgress["segments"] = [
  { key: "inDraft", label: "In Draft", count: 0 },
  { key: "routing", label: "Routing", count: 0 },
  { key: "live", label: "Live", count: 0 },
  { key: "remaining", label: "Remaining", count: 0 },
];

const FALLBACK_USER_WORKLOAD: DashboardUserWorkload = {
  assignedTaskCount: 0,
  assignedOverdueTaskCount: 0,
  assignedDueSoonTaskCount: 0,
  assignedOtherOpenTaskCount: 0,
  unresolvedCommentCount: 0,
  total: 0,
};

const OVERALL_PROGRESS_TOOLTIP_TITLE = [
  "All documents that require completion and approval.",
  "Meaning of the statuses:",
  "Draft: Work on document started.",
  "Routing: Document submitted for approval but not signed by all approvers.",
  "Live: Document signed by all approvers.",
  "Remaining: Work on document not started.",
].join("\n");

const getPositiveCount = (value: number) => Math.max(0, value);

const getPercentage = (count: number, total: number) =>
  total > 0 ? (count / total) * 100 : 0;

const getDisplayPercentage = (count: number, total: number) =>
  Math.round(getPercentage(count, total));

const getProgressSegments = (
  progress?: DashboardOverallProgress
): DashboardProgressSegment[] =>
  (progress?.segments ?? FALLBACK_SEGMENTS).map((segment) => ({
    key: segment.key,
    label: segment.label,
    count: getPositiveCount(segment.count),
    colorHex: SEGMENT_COLORS[segment.key],
    testIdKey: SEGMENT_TEST_ID_KEYS[segment.key],
  }));

const formatCountLabel = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const formatConjunctionList = (items: string[]) => {
  if (items.length <= 1) {
    return items[0] ?? "";
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const formatWorkloadContext = ({
  assignedDueSoonTaskCount,
  assignedOtherOpenTaskCount,
  assignedOverdueTaskCount,
  unresolvedCommentCount,
}: DashboardUserWorkload) => {
  const workloadItems = [
    assignedOverdueTaskCount > 0
      ? formatCountLabel(
          assignedOverdueTaskCount,
          "overdue task",
          "overdue tasks"
        )
      : null,
    assignedDueSoonTaskCount > 0
      ? formatCountLabel(
          assignedDueSoonTaskCount,
          "due soon task",
          "due soon tasks"
        )
      : null,
    assignedOtherOpenTaskCount > 0
      ? formatCountLabel(
          assignedOtherOpenTaskCount,
          "open task",
          "open tasks"
        )
      : null,
    unresolvedCommentCount > 0
      ? formatCountLabel(
          unresolvedCommentCount,
          "unresolved comment",
          "unresolved comments"
        )
      : null,
  ].filter((item): item is string => item !== null);

  if (workloadItems.length === 0) {
    return "No assigned tasks or unresolved comments need attention.";
  }

  return `${formatConjunctionList(workloadItems)} need attention.`;
};

const formatDashboardDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);

const formatAttentionHeading = (
  count: number,
  firstName: string | undefined
): [string, string] => {
  const attentionCount = formatCountLabel(count, "thing", "things");

  if (firstName) {
    return [`Hi ${firstName}, let's get`, `${attentionCount} off your plate.`];
  }

  return ["Let's get", `${attentionCount} off your plate.`];
};

export const DashboardOverallProgressCard = ({
  title,
  progress,
  userWorkload = FALLBACK_USER_WORKLOAD,
  firstName,
  className,
}: DashboardOverallProgressCardProps) => {
  const rootClassName = ["min-w-0", className].filter(Boolean).join(" ");
  const totalDocuments = getPositiveCount(progress?.total ?? 0);
  const segments = getProgressSegments(progress);
  const greetingName = firstName?.trim();
  const dashboardSubheader = formatDashboardDate(new Date());
  const [attentionHeadingIntro, attentionHeadingCount] = formatAttentionHeading(
    userWorkload.total,
    greetingName
  );
  const documentsInProgress = segments
    .filter((segment) => segment.key !== "remaining")
    .reduce((total, segment) => total + segment.count, 0);
  const completionPercentage = getDisplayPercentage(
    documentsInProgress,
    totalDocuments
  );

  return (
    <section
      className={rootClassName}
      aria-label={title}
      data-testid="dashboard-overall-progress-heading"
    >
      <div className="flex min-w-0 flex-col gap-7">
        <div className="flex min-w-0 flex-col gap-4">
          <Typography
            variant="overline"
            component="p"
            className="font-semibold normal-case text-[#0052ff]"
            sx={{
              fontSize: "0.75rem",
              letterSpacing: "0.12em",
              lineHeight: 1.1,
              overflowWrap: "anywhere",
            }}
          >
            {dashboardSubheader}
          </Typography>
          <Typography
            variant="h2"
            className="font-semibold text-gray-950"
            sx={{
              fontSize: "2.5rem",
              letterSpacing: 0,
              lineHeight: 1.25,
              overflowWrap: "anywhere",
            }}
          >
            {attentionHeadingIntro}
            <br />
            {attentionHeadingCount}
          </Typography>

          <div className="flex min-w-0 flex-col gap-1">
            <Typography
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                letterSpacing: 0,
                lineHeight: 1.35,
                overflowWrap: "anywhere",
              }}
            >
              {formatWorkloadContext(userWorkload)}
            </Typography>
            <Typography
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                letterSpacing: 0,
                lineHeight: 1.35,
                overflowWrap: "anywhere",
              }}
            >
              Here's where the system stands today.
            </Typography>
          </div>
        </div>

        <div
          className="flex min-w-0 flex-col gap-4"
          data-testid="dashboard-overall-progress-graph"
        >
          {totalDocuments === 0 ? (
            <DashboardEmptyStateText>
              No document progress available.
            </DashboardEmptyStateText>
          ) : (
            <>
              <div className="flex min-w-0 flex-wrap items-end gap-x-2 gap-y-1">
                <Typography
                  variant="h3"
                  component="p"
                  className="font-semibold text-gray-950"
                  sx={{
                    fontSize: "2.25rem",
                    letterSpacing: 0,
                    lineHeight: 0.95,
                  }}
                >
                  {documentsInProgress}
                </Typography>
                <Typography
                  variant="h6"
                  component="div"
                  color="text.secondary"
                  className="inline-flex min-w-0 items-center gap-1"
                  sx={{
                    pb: 0.5,
                    fontSize: "1rem",
                    fontWeight: 400,
                    letterSpacing: 0,
                    lineHeight: 1.2,
                  }}
                >
                  roadmap documents &middot;{" "}
                  <BoxlessStrong>
                    {completionPercentage}% complete
                  </BoxlessStrong>
                  <Tooltip
                    title={OVERALL_PROGRESS_TOOLTIP_TITLE}
                    arrow
                    describeChild
                    slotProps={{
                      tooltip: {
                        sx: { whiteSpace: "pre-line" },
                      },
                    }}
                  >
                    <IconButton
                      aria-label="Overall progress details"
                      size="small"
                      data-testid="dashboard-overall-progress-info"
                      sx={{
                        color: "text.secondary",
                        p: 0.25,
                      }}
                    >
                      <InfoOutlined sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </div>

              <div
                className="flex h-3 w-full min-w-0 overflow-hidden rounded-full bg-gray-100"
                role="progressbar"
                aria-label={`${documentsInProgress} of ${totalDocuments} roadmap documents are being worked on`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={completionPercentage}
                data-testid="dashboard-overall-progress-bar"
              >
                {segments.map((segment) => {
                  const widthPercentage = getPercentage(
                    segment.count,
                    totalDocuments
                  );

                  if (widthPercentage === 0) {
                    return null;
                  }

                  return (
                    <span
                      key={segment.key}
                      className="h-full"
                      data-testid={`dashboard-overall-progress-bar-segment-${segment.testIdKey}`}
                      style={{
                        width: `${widthPercentage}%`,
                        backgroundColor: segment.colorHex,
                      }}
                    />
                  );
                })}
              </div>

              <div
                className="flex min-w-0 flex-wrap gap-x-6 gap-y-3"
                data-testid="dashboard-overall-progress-legend"
              >
                {segments.map((segment) => {
                  const displayPercentage = getDisplayPercentage(
                    segment.count,
                    totalDocuments
                  );
                  const tooltipTitle = `${segment.label}: ${segment.count} of ${totalDocuments} roadmap documents (${displayPercentage}%).`;

                  return (
                    <Tooltip
                      key={segment.key}
                      title={tooltipTitle}
                      arrow
                      describeChild
                    >
                      <div
                        className="inline-flex max-w-full min-w-0 items-center gap-2"
                        data-testid={`dashboard-overall-progress-segment-${segment.testIdKey}`}
                      >
                        <span
                          className="h-3 w-3 shrink-0 rounded"
                          style={{ backgroundColor: segment.colorHex }}
                          aria-hidden
                        />
                        <Typography
                          variant="body1"
                          component="span"
                          color="text.secondary"
                          className="min-w-0"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          {segment.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          component="span"
                          className="shrink-0 font-semibold text-gray-950"
                        >
                          {displayPercentage}%
                        </Typography>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const BoxlessStrong = ({ children }: { children: ReactNode }) => (
  <Typography
    component="span"
    variant="inherit"
    className="font-semibold text-gray-950"
  >
    {children}
  </Typography>
);
